import { ReactElement } from "react";
import { useState } from "react";

import { css } from "@compiled/react";

import { token } from "@atlaskit/tokens";

import { ProductKey } from "@/components/signup/utils";
import { wacToken } from "@/libraries/design/foundations";
import { LogoV3 } from "@/libraries/wpl/foundations/src";

import { HoverProductCard, ProductApp } from "./hover-product-card";

const containerCss = css({
    display: "flex",
    flexDirection: "column",
    width: "100%",
    position: "relative"
});

const hoverCardContainerCss = css({
    position: "absolute",
    insetBlockStart: "100%",
    insetInlineEnd: 0,
    marginBlockStart: token("space.100"),
    zIndex: 1000
});

const cardCss = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    justifyContent: "center",
    padding: token("space.100"),
    backgroundColor: token("color.background.input"),
    border: `1px solid ${token("color.border")}`,
    borderRadius: token("space.075"),
    width: "100%",
    boxSizing: "border-box"
});

const contentRowCss = css({
    display: "flex",
    gap: "15px",
    alignItems: "center",
    width: "100%"
});

const logoSectionCss = css({
    display: "flex",
    flex: "1 0 0",
    alignItems: "center",
    minHeight: "0px",
    minWidth: "0px",
    paddingBlockEnd: token("space.0"),
    paddingInlineStart: token("space.0"),
    paddingInlineEnd: token("space.0"),
    paddingBlockStart: token("space.0")
});

const logoContainerCss = css({
    height: "40px",
    width: "0px",
    flexShrink: 0
});

const logoContentCss = css({
    display: "flex",
    flex: "1 0 0",
    gap: token("space.100"),
    alignItems: "center",
    minHeight: "0px",
    minWidth: "0px"
});

const logoIconCss = css({
    "display": "flex",
    "flexDirection": "column",
    "alignItems": "flex-start",
    "overflow": "hidden",
    "width": token("space.300"),
    "height": token("space.300"),
    "flexShrink": 0,
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "& svg": {
        width: token("space.300"),
        height: token("space.300")
    }
});

const textContentCss = css({
    display: "flex",
    flex: "1 0 0",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "center",
    minHeight: token("space.0"),
    minWidth: token("space.0"),
    color: wacToken("color.neutral.dark500"),
    whiteSpace: "pre-wrap"
});

const titleCss = css({
    margin: 0,
    fontSize: "0.875rem",
    fontWeight: 500,
    lineHeight: "1.25rem",
    color: token("color.text.subtle"),
    fontFamily: "Atlassian Sans",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
});

const subtitleCss = css({
    margin: 0,
    fontSize: "0.75rem",
    lineHeight: "1rem",
    color: wacToken("color.neutral.light700"),
    fontFamily: "Atlassian Sans",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
});

const dataTileCss = css({
    display: "flex",
    flexDirection: "column",
    gap: token("space.075"),
    alignItems: "flex-end",
    justifyContent: "center",
    paddingX: token("space.0"),
    paddingY: token("space.0"),
    flexShrink: 0,
    width: "170px"
});

const appsContainerCss = css({
    display: "flex",
    gap: token("space.100"),
    alignItems: "center",
    justifyContent: "flex-end",
    paddingX: token("space.0"),
    paddingY: token("space.100"),
    flexShrink: 0,
    width: "138px"
});

const appSetCss = css({
    display: "flex",
    alignItems: "flex-start",
    flexShrink: 0
});

const appSetInnerCss = css({
    display: "flex",
    gap: token("space.050"),
    alignItems: "center",
    justifyContent: "flex-end"
});

const productMarkStyles = css({
    "display": "flex",
    "alignItems": "center",
    "justifyContent": "center",
    "width": "44px",
    "height": "44px",
    "padding": "4px",
    "backgroundColor": token("color.background.neutral.subtle"),
    "borderRadius": "4px",
    "marginInlineEnd": "12px",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "& svg": {
        width: token("space.400"),
        height: token("space.400")
    }
});

export interface ProductData {
    productKey: string;
    productName: string;
    tier?: "Free" | "Standard" | "Premium";
    productMark: ReactElement;
    sideIcons?: ReactElement[];
}

// Map brand names to product names for hover cards
const brandToProductNameMap: Record<string, string> = {
    jira: "Jira",
    confluence: "Confluence",
    loom: "Loom",
    jiraservicemanagement: "Jira Service Management",
    customerservicemanagement: "Jira Customer Service",
    focus: "Focus",
    talent: "Talent",
    jiraalign: "Jira Align"
};

// Check if a product is a collection
const isCollection = (productName: string): boolean => {
    return (
        productName === "Teamwork Collection" ||
        productName === "Service Collection" ||
        productName === "Strategy Collection"
    );
};

// Extract brand from a LogoV3 ReactElement
const extractBrandFromIcon = (icon: ReactElement): string | null => {
    if (icon && icon.props && icon.props.brand) {
        return icon.props.brand;
    }
    return null;
};

const isProductKeyBrand = (productKey: ProductKey, brand: string): boolean => {
    switch (productKey) {
        // TWC Side Icons
        case ProductKey.JIRA_SOFTWARE:
            return brand === "jira";
        case ProductKey.CONFLUENCE:
            return brand === "confluence";
        case ProductKey.LOOM:
            return brand === "loom";
        // Service Collection Side Icons
        case ProductKey.JIRA_SERVICE_MANAGEMENT:
            return brand === "jiraservicemanagement";
        case ProductKey.JIRA_CUSTOMER_SERVICE:
            return brand === "customerservicemanagement";
        // Strategy Collection Side Icons
        // case ProductKey.FOCUS:
        //     return brand === "focus";
        // case ProductKey.TALENT:
        //     return brand === "talent";
        // case ProductKey.JIRA_ALIGN:
        //     return brand === "jiraalign";
        default:
            return false;
    }
};

// Convert sideIcons to ProductApp format for HoverProductCard
const convertSideIconsToApps = (
    sideIcons: ReactElement[],
    includedProductKeys?: string[]
): ProductApp[] => {
    const apps: ProductApp[] = [];
    sideIcons.forEach((icon, index) => {
        const settingUpCurrently = !!includedProductKeys?.some((key) =>
            isProductKeyBrand(key as ProductKey, icon.props.brand)
        );
        const brand = extractBrandFromIcon(icon);
        if (brand) {
            const productName = brandToProductNameMap[brand] || brand;
            apps.push({
                id: `${brand}-${index}`,
                name: productName,
                icon: icon,
                settingUpCurrently
            });
        }
    });
    return apps;
};

const AppIcon = () => {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
        >
            <path
                d="M0 6C0 2.68629 2.68629 0 6 0H18C21.3137 0 24 2.68629 24 6V18C24 21.3137 21.3137 24 18 24H6C2.68629 24 0 21.3137 0 18V6Z"
                fill="#DDDEE1"
            />
            <path d="M11.125 5.875H5.875V11.125H11.125V5.875Z" fill="#101214" />
            <path
                d="M11.125 12.875H5.875V18.125H11.125V12.875Z"
                fill="#101214"
            />
            <path
                d="M12.875 5.875H18.125V11.125H12.875V5.875Z"
                fill="#101214"
            />
            <path
                d="M18.125 12.875H12.875V18.125H18.125V12.875Z"
                fill="#101214"
            />
        </svg>
    );
};

export interface ProductCardProps {
    comprehensiveProductData?: ProductData[];
    // Individual product props for single card usage
    productName?: string;
    tier?: "Free" | "Standard" | "Premium";
    includedProductKeys?: string[];
}

const ComprehensiveProductData = (): ProductData[] => {
    return [
        {
            productKey: "jira",
            productName: "Jira",
            tier: "Free",
            productMark: <LogoV3 brand="jira" type="mark" appearance="brand" />,
            sideIcons: [<AppIcon key="app-icon" />]
        },
        {
            productKey: "jsm",
            productName: "Jira Service Management",
            tier: "Standard",
            productMark: (
                <LogoV3
                    brand="jiraservicemanagement"
                    type="mark"
                    appearance="brand"
                    css={productMarkStyles}
                />
            ),
            sideIcons: [<AppIcon key="app-icon" />]
        },
        {
            productKey: "confluence",
            productName: "Confluence",
            tier: "Free",
            productMark: (
                <LogoV3 brand="confluence" type="mark" appearance="brand" />
            ),
            sideIcons: [<AppIcon key="app-icon" />]
        },
        {
            productKey: "jpd",
            productName: "Jira Product Discovery",
            tier: "Premium",
            productMark: (
                <LogoV3
                    brand="jiraproductdiscovery"
                    type="mark"
                    appearance="brand"
                />
            ),
            sideIcons: [<AppIcon key="app-icon" />]
        },
        {
            productKey: "rovo",
            productName: "Rovo",
            tier: "Premium",
            productMark: <LogoV3 brand="rovo" type="mark" appearance="brand" />,
            sideIcons: [<AppIcon key="app-icon" />]
        },
        {
            productKey: "loom",
            productName: "Loom",
            tier: "Standard",
            productMark: <LogoV3 brand="loom" type="mark" appearance="brand" />,
            sideIcons: [<AppIcon key="app-icon" />]
        },
        {
            productKey: "compass",
            productName: "Compass",
            tier: "Free",
            productMark: (
                <LogoV3 brand="compass" type="mark" appearance="brand" />
            ),
            sideIcons: [<AppIcon key="app-icon" />]
        },
        {
            productKey: "twc",
            productName: "Teamwork Collection",
            tier: "Premium",
            productMark: (
                <LogoV3 brand="atlassian" type="mark" appearance="brand" />
            ),
            sideIcons: [
                <LogoV3
                    key="confluence"
                    brand="confluence"
                    type="mark"
                    appearance="brand"
                />,
                <LogoV3
                    key="jira"
                    brand="jira"
                    type="mark"
                    appearance="brand"
                />,
                <LogoV3
                    key="loom"
                    brand="loom"
                    type="mark"
                    appearance="brand"
                />
            ]
        },
        {
            productKey: "stratco",
            productName: "Strategy Collection",
            tier: "Standard",
            productMark: (
                <LogoV3 brand="atlassian" type="mark" appearance="brand" />
            ),
            sideIcons: [
                <LogoV3
                    key="focus"
                    brand="focus"
                    type="mark"
                    appearance="brand"
                />,
                <LogoV3
                    key="talent"
                    brand="talent"
                    type="mark"
                    appearance="brand"
                />,
                <LogoV3
                    key="jiraalign"
                    brand="jiraalign"
                    type="mark"
                    appearance="brand"
                />
            ]
        },
        {
            productKey: "servco",
            productName: "Service Collection",
            tier: "Premium",
            productMark: (
                <LogoV3 brand="atlassian" type="mark" appearance="brand" />
            ),
            sideIcons: [
                <LogoV3
                    key="jiraservicemanagement"
                    brand="jiraservicemanagement"
                    type="mark"
                    appearance="brand"
                />,
                <LogoV3
                    key="customerservicemanagement"
                    brand="customerservicemanagement"
                    type="mark"
                    appearance="brand"
                />
            ]
        }
    ];
};

// Individual product card component with hover functionality
const ProductCardItem = ({
    product,
    includedProductKeys
}: {
    product: ProductData;
    includedProductKeys?: string[];
}) => {
    const [productCardHovered, setProductCardHovered] = useState(false);
    const isProductCollection = isCollection(product.productName);
    const apps =
        isProductCollection && product.sideIcons
            ? convertSideIconsToApps(product.sideIcons, includedProductKeys)
            : [];

    const handleProductCardHover = () => {
        if (isProductCollection) {
            setProductCardHovered(true);
        }
    };

    const handleProductCardHoverLeave = () => {
        setProductCardHovered(false);
    };

    return (
        <div
            css={containerCss}
            onMouseEnter={handleProductCardHover}
            onMouseLeave={handleProductCardHoverLeave}
            role="presentation"
        >
            <div css={cardCss}>
                <div css={contentRowCss}>
                    <div css={logoSectionCss}>
                        <div css={logoContainerCss} />
                        <div css={logoContentCss}>
                            <div css={logoIconCss}>{product.productMark}</div>
                            <div css={textContentCss}>
                                <p css={titleCss}>{product.productName}</p>
                                <p css={subtitleCss}>{product.tier}</p>
                            </div>
                        </div>
                    </div>
                    <div css={dataTileCss}>
                        <div css={appsContainerCss}>
                            <div css={appSetCss}>
                                <div css={appSetInnerCss}>
                                    {product.sideIcons &&
                                        product.sideIcons.length > 0 &&
                                        product.sideIcons.map((icon, index) => (
                                            <div key={index}>
                                                <div
                                                    css={css({
                                                        "display": "flex",
                                                        "alignItems": "center",
                                                        "justifyContent":
                                                            "center",
                                                        "width":
                                                            token("space.300"),
                                                        "height":
                                                            token("space.300"),
                                                        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                                                        "& svg": {
                                                            width: token(
                                                                "space.300"
                                                            ),
                                                            height: token(
                                                                "space.300"
                                                            )
                                                        }
                                                    })}
                                                >
                                                    {icon}
                                                </div>
                                            </div>
                                        ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {productCardHovered && isProductCollection && apps.length > 0 && (
                <div css={hoverCardContainerCss}>
                    <HoverProductCard title={"Included apps"} apps={apps} />
                </div>
            )}
        </div>
    );
};

export const ProductCard = ({
    comprehensiveProductData = ComprehensiveProductData(),
    productName,
    tier,
    includedProductKeys
}: ProductCardProps) => {
    // If individual product props are provided, find the matching product
    let productsToRender = comprehensiveProductData;

    if (productName) {
        const matchingProduct = comprehensiveProductData.find(
            (product) => product.productName === productName
        );
        if (matchingProduct) {
            // If tier is provided, override the tier from comprehensiveProductData
            const productWithTier = tier
                ? { ...matchingProduct, tier }
                : matchingProduct;
            productsToRender = [productWithTier];
        } else {
            // If product not found in comprehensiveProductData but productName and tier are provided,
            // create a basic product entry
            if (tier) {
                productsToRender = [
                    {
                        productKey: productName
                            .toLowerCase()
                            .replace(/\s+/g, "-"),
                        productName,
                        tier,
                        productMark: (
                            <LogoV3
                                brand="atlassian"
                                type="mark"
                                appearance="brand"
                            />
                        ),
                        sideIcons: []
                    }
                ];
            } else {
                productsToRender = [];
            }
        }
    }

    return (
        <>
            {productsToRender.map((product) => (
                <ProductCardItem
                    key={product.productKey}
                    product={product}
                    includedProductKeys={includedProductKeys}
                />
            ))}
        </>
    );
};
